import React, { useContext } from "react";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { LogContext } from "context/log";
import { CollectionLayout } from "@pepdirect/ui/layout/CollectionLayout";
import { Heading } from "@pepdirect/ui/heading";
import { BundleTile } from "components/BundleTile";
import createSsrClient from "services/graphql/ssr-client";
import {
  StoryBySlugDocument,
  StoryBySlugQuery,
  StoryBySlugQueryVariables,
} from "services/graphql/generated";
import { useSaveScroll } from "@pepdirect/helpers/useScrollRestoration";
import { GqlStory } from "types/story";
import { PAGE_TYPE_ENUM, Product } from "@pepdirect/shared/types";
import { META_TITLE, CANONICAL_URL_BASE } from "constants/meta";
import st from "./kit.module.scss";

interface KitsPageProps {
  story?: GqlStory | null;
}

export const getServerSideProps: GetServerSideProps<KitsPageProps> = async (
  appContext
) => {
  const gqlClient: ApolloClient<NormalizedCacheObject> = createSsrClient(
    appContext.req
  );

  try {
    const { data } = await gqlClient.query<
      StoryBySlugQuery,
      StoryBySlugQueryVariables
    >({
      query: StoryBySlugDocument,
      variables: { slug: appContext.params?.slug as string },
    });
    return {
      props: {
        story: data.story,
      },
    };
  } catch (e) {
    // We don't need to capture 404, do we?
    if (e instanceof Error && e.message === "not_found") {
      return {
        notFound: true,
      };
    } else {
      // Re-throw so there'll be 500 error on the page
      throw e;
    }
  }
};

export default function Kit({ story }: KitsPageProps): JSX.Element {
  const { useLogPageView } = useContext(LogContext);

  const router = useRouter();
  useSaveScroll(router);

  const title = story?.title2 || story?.title1 || "";
  const metaTitle = `${title} – ${META_TITLE}`;
  const metaUrl = `${CANONICAL_URL_BASE}/kit/${story?.slug}`;

  useLogPageView(
    {
      pageType: PAGE_TYPE_ENUM.collection,
      pageTitle: metaTitle,
      pageCategory: title || undefined,
      id: story?.id || "",
    },
    undefined,
    undefined,
    story?.slug || ""
  );

  return (
    <div className={st.wrapper}>
      <Head>
        <title>{metaTitle}</title>
        <meta name="og:title" content={metaTitle} key="og:title" />
        <link rel="canonical" href={metaUrl} />
        <meta name="og:url" content={metaUrl} key="og:url" />
      </Head>
      <div className={st.title}>
        <Heading level="h1" size="xl">
          {title}
        </Heading>
      </div>
      <CollectionLayout>
        {!!story?.items?.length &&
          story?.items?.length > 0 &&
          story.items
            .filter((x) => x)
            .map((item) => (
              <BundleTile
                item={item as Product}
                key={item?._id}
                collection={title}
              />
            ))}
      </CollectionLayout>
    </div>
  );
}
